import React, { useState, useEffect, useCallback, useContext } from "react";
import { ThemeContext } from "../../../../context/ThemeContext";
import { getData, serverURL } from "../../../../services/Fetchnodeservices";

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const AttachmentIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const TicketIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    <path d="M13 5v2M13 17v2M13 11v2" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// HELPER FUNCTION TO GET IMAGE URL
// ─────────────────────────────────────────────────────────────────────────────
const getImageUrl = (filename) => {
  if (!filename) return null;
  if (filename.startsWith('http://') || filename.startsWith('https://')) {
    return filename;
  }
  let cleanFilename = filename;
  if (filename.includes('/')) {
    cleanFilename = filename.split('/').pop();
  }
  return `${serverURL}/public/uploadnew/upload/${cleanFilename}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// VIEW TICKET DIALOG COMPONENT (Theme-Aware)
// ─────────────────────────────────────────────────────────────────────────────
const ViewTicketDialog = ({ onClose, ticket, onStatusUpdate, theme }) => {
  const [status, setStatus] = useState(ticket?.ticketstatus || "open");
  const [updating, setUpdating] = useState(false);
  const [imageError, setImageError] = useState(false);

  const getUserName = () => {
    if (ticket.user_table === "resturant") return `Restaurant #${ticket.user_id}`;
    if (ticket.user_table === "user") return `User #${ticket.user_id}`;
    if (ticket.user_table === "rider") return `Rider #${ticket.user_id}`;
    return `${ticket.user_table || "Unknown"} #${ticket.user_id}`;
  };

  const screenshotUrl = getImageUrl(ticket.screenshot);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(8px)",
        animation: "fadeIn 0.2s ease-out",
        padding: "20px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "90%",
          maxWidth: "550px",
          background: theme.surface,
          borderRadius: "20px",
          boxShadow: `0 25px 60px ${theme.primary}30`,
          overflow: "hidden",
          animation: "slideUp 0.3s ease-out",
          border: `1px solid ${theme.border}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "18px 24px",
            borderBottom: `1px solid ${theme.border}`,
            background: theme.surfaceLighter,
          }}
        >
          <h3 style={{ fontSize: "18px", fontWeight: 700, color: theme.textPrimary, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: theme.primary, fontFamily: 'monospace' }}>Ticket #{ticket.id}</span>
          </h3>
          <button
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: theme.textMuted,
              padding: "6px",
              display: "flex",
              borderRadius: "8px",
              transition: "all 0.2s",
            }}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `${theme.error || theme.danger}15`;
              e.currentTarget.style.color = theme.error || theme.danger;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "none";
              e.currentTarget.style.color = theme.textMuted;
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "24px", maxHeight: "60vh", overflowY: "auto" }}>
          {/* Source */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: theme.textMuted, marginBottom: "6px" }}>Source</div>
            <div style={{ fontSize: "14px", color: theme.textPrimary, fontWeight: 500 }}>{getUserName()}</div>
          </div>

          {/* Subject */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: theme.textMuted, marginBottom: "6px" }}>Subject</div>
            <div style={{ fontSize: "14px", color: theme.textPrimary, fontWeight: 600 }}>{ticket.subject}</div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: theme.textMuted, marginBottom: "6px" }}>Description</div>
            <div style={{
              fontSize: "13px",
              color: theme.textSecondary,
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
              background: theme.surfaceLighter,
              padding: "12px 16px",
              borderRadius: "10px",
              border: `1px solid ${theme.border}`,
            }}>
              {ticket.description}
            </div>
          </div>

          {/* Status */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: theme.textMuted, marginBottom: "6px" }}>Status</div>
            <select
              style={{
                padding: "10px 14px",
                borderRadius: "10px",
                fontSize: "13px",
                background: theme.surfaceLighter,
                border: `1px solid ${theme.border}`,
                color: theme.textPrimary,
                cursor: "pointer",
                width: "100%",
                outline: "none",
                transition: "border-color 0.2s",
              }}
              value={status}
              disabled={updating}
              onFocus={(e) => (e.currentTarget.style.borderColor = theme.primary)}
              onBlur={(e) => (e.currentTarget.style.borderColor = theme.border)}
            >
              <option value="open">🔵 Open</option>
              <option value="in_progress">🟡 In Progress</option>
              <option value="closed">🟢 Closed</option>
            </select>
          </div>

          {/* Screenshot */}
          {screenshotUrl && !imageError && (
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: theme.textMuted, marginBottom: "6px" }}>Screenshot</div>
              <img
                src={screenshotUrl}
                alt="Screenshot"
                style={{
                  maxWidth: "100%",
                  maxHeight: "250px",
                  borderRadius: "12px",
                  marginTop: "8px",
                  objectFit: "contain",
                  background: theme.surfaceLighter,
                  padding: "8px",
                  border: `1px solid ${theme.border}`,
                }}
                onError={() => setImageError(true)}
              />
            </div>
          )}
          {screenshotUrl && imageError && (
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: theme.textMuted, marginBottom: "6px" }}>Screenshot</div>
              <div style={{
                color: theme.error || theme.danger,
                fontSize: "12px",
                padding: "12px",
                background: `${theme.error || theme.danger}15`,
                borderRadius: "10px",
                border: `1px solid ${theme.error || theme.danger}30`,
              }}>
                ⚠️ Image not found: {ticket.screenshot}
              </div>
            </div>
          )}

          {/* Dates */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: theme.textMuted, marginBottom: "6px" }}>Created At</div>
            <div style={{ fontSize: "13px", color: theme.textSecondary }}>{new Date(ticket.created_at).toLocaleString()}</div>
          </div>
          {ticket.updated_at && ticket.updated_at !== ticket.created_at && (
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: theme.textMuted, marginBottom: "6px" }}>Last Updated</div>
              <div style={{ fontSize: "13px", color: theme.textSecondary }}>{new Date(ticket.updated_at).toLocaleString()}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "16px 24px",
            borderTop: `1px solid ${theme.border}`,
            background: theme.surfaceLighter,
          }}
        >
          <button
            style={{
              padding: "10px 24px",
              background: theme.primary,
              border: "none",
              borderRadius: "10px",
              fontSize: "13px",
              fontWeight: 600,
              color: "#fff",
              cursor: "pointer",
              boxShadow: `0 4px 12px ${theme.primary}40`,
              transition: "all 0.2s ease",
            }}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = `0 6px 16px ${theme.primary}60`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}40`;
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS (Theme-Aware)
// ─────────────────────────────────────────────────────────────────────────────

const StatusBadge = ({ status, theme }) => {
  const config = {
    open: { bg: `${theme.primary}1a`, color: theme.primary, label: "Open", dot: theme.primary },
    in_progress: { bg: `${theme.warning}1a`, color: theme.warning, label: "In Progress", dot: theme.warning },
    closed: { bg: `${theme.success}1a`, color: theme.success, label: "Closed", dot: theme.success },
  };
  const c = config[status] || config.open;
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      padding: "4px 12px",
      borderRadius: "20px",
      fontSize: "11px",
      fontWeight: 600,
      background: c.bg,
      color: c.color,
    }}>
      <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: c.dot }} />
      {c.label}
    </span>
  );
};

const SourceBadge = ({ userTable, theme }) => {
  const config = {
    user: { bg: `#8b5cf61a`, color: "#8b5cf6", label: "User" },
    resturant: { bg: `#ec48991a`, color: "#ec4899", label: "Restaurant" },
    rider: { bg: `#14b8a61a`, color: "#14b8a6", label: "Rider" },
  };
  const c = config[userTable] || { bg: `${theme.textMuted}1a`, color: theme.textMuted, label: "Other" };
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "10px",
      fontWeight: 600,
      background: c.bg,
      color: c.color,
    }}>
      {c.label}
    </span>
  );
};

const StatCard = ({ title, count, color, icon, theme }) => (
  <div
    style={{
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "14px",
      padding: "18px 20px",
      textAlign: "center",
      minWidth: "100px",
      flex: 1,
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      cursor: "pointer",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = `0 8px 20px ${color}15`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    }}
  >
    {icon && (
      <div
        style={{
          width: "36px",
          height: "36px",
          borderRadius: "10px",
          background: `${color}1a`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: color,
          margin: "0 auto 10px",
          fontSize: "16px",
        }}
      >
        {icon}
      </div>
    )}
    <div style={{ fontSize: "26px", fontWeight: 700, color: theme.textPrimary }}>{count}</div>
    <div style={{ fontSize: "11px", fontWeight: 500, color: theme.textMuted, marginTop: "4px" }}>{title}</div>
  </div>
);

const FilterButton = ({ label, isActive, onClick, theme }) => (
  <button
    onClick={onClick}
    style={{
      padding: "6px 16px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: 500,
      background: isActive ? theme.primary : "transparent",
      color: isActive ? "#fff" : theme.textSecondary,
      border: isActive ? `1px solid ${theme.primary}` : `1px solid ${theme.border}`,
      cursor: "pointer",
      transition: "all 0.2s ease",
    }}
    onMouseEnter={(e) => {
      if (!isActive) {
        e.currentTarget.style.background = theme.surfaceLighter;
        e.currentTarget.style.color = theme.textPrimary;
      }
    }}
    onMouseLeave={(e) => {
      if (!isActive) {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = theme.textSecondary;
      }
    }}
  >
    {label}
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT (Theme-Aware)
// ─────────────────────────────────────────────────────────────────────────────
const TicketsTable = () => {
  const { theme } = useContext(ThemeContext); // ← Theme Context
  
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sourceFilter, setSourceFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const itemsPerPage = 10;

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getData("supportticket/Tickets");
      const ticketsData = response.data || [];
      const sortedTickets = [...ticketsData].sort((a, b) => b.id - a.id);
      setTickets(sortedTickets);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.ticketstatus === "open").length,
    inProgress: tickets.filter(t => t.ticketstatus === "in_progress").length,
    closed: tickets.filter(t => t.ticketstatus === "closed").length,
    users: tickets.filter(t => t.user_table === "user").length,
    restaurants: tickets.filter(t => t.user_table === "resturant").length,
    riders: tickets.filter(t => t.user_table === "rider").length,
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch =
      ticket.subject?.toLowerCase().includes(search.toLowerCase()) ||
      ticket.description?.toLowerCase().includes(search.toLowerCase()) ||
      ticket.id.toString().includes(search);
    const matchesStatus = statusFilter === "All" || ticket.ticketstatus === statusFilter;
    const matchesSource = sourceFilter === "All" || ticket.user_table === sourceFilter;
    return matchesSearch && matchesStatus && matchesSource;
  });

  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
  const paginatedTickets = filteredTickets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setIsViewDialogOpen(true);
  };

  const getUserDisplayName = (ticket) => {
    if (ticket.user_table === "resturant") return `Restaurant #${ticket.user_id}`;
    if (ticket.user_table === "user") return `User #${ticket.user_id}`;
    if (ticket.user_table === "rider") return `Rider #${ticket.user_id}`;
    return `${ticket.user_table || "Unknown"} #${ticket.user_id}`;
  };

  if (loading) {
    return (
      <div style={{ padding: "20px 24px", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ textAlign: "center", padding: "60px", background: theme.surface, borderRadius: "16px", border: `1px solid ${theme.border}` }}>
          <div style={{
            width: "40px",
            height: "40px",
            border: `3px solid ${theme.border}`,
            borderTopColor: theme.primary,
            borderRadius: "50%",
            margin: "0 auto 16px",
            animation: "spin 1s linear infinite",
          }} />
          <p style={{ color: theme.textMuted, fontSize: "14px", margin: 0 }}>Loading tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "transparent", fontFamily: "'DM Sans', sans-serif", padding: "20px 24px" }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Stats Cards Row */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap", alignItems: "stretch" }}>
        {/* Status Stats */}
        <div style={{ display: "flex", gap: "12px", flex: 2, flexWrap: "wrap" }}>
          <StatCard title="Total" count={stats.total} color={theme.primary} icon="🎫" theme={theme} />
          <StatCard title="Open" count={stats.open} color={theme.primary} icon="🔵" theme={theme} />
          <StatCard title="In Progress" count={stats.inProgress} color={theme.warning} icon="🟡" theme={theme} />
          <StatCard title="Closed" count={stats.closed} color={theme.success} icon="🟢" theme={theme} />
        </div>
        
        {/* Divider */}
        <div style={{ width: "1px", background: theme.border, margin: "0 4px", alignSelf: "stretch" }} />
        
        {/* Source Stats */}
        <div style={{ display: "flex", gap: "12px", flex: 2, flexWrap: "wrap" }}>
          <StatCard title="All Sources" count={stats.total} color="#8b5cf6" icon="📊" theme={theme} />
          <StatCard title="Users" count={stats.users} color="#8b5cf6" icon="👤" theme={theme} />
          <StatCard title="Restaurants" count={stats.restaurants} color="#ec4899" icon="🍽️" theme={theme} />
          <StatCard title="Riders" count={stats.riders} color="#14b8a6" icon="🚴" theme={theme} />
        </div>
      </div>

      {/* Search and Status Filters */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "16px" }}>
        {/* Search */}
        <div style={{ position: "relative", flex: 1, maxWidth: "400px", minWidth: "250px" }}>
          <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: theme.primary, display: "flex", alignItems: "center" }}>
            <SearchIcon />
          </span>
          <input
            type="text"
            style={{
              width: "100%",
              padding: "10px 12px 10px 38px",
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: "10px",
              fontSize: "13px",
              color: theme.textPrimary,
              outline: "none",
              transition: "all 0.2s ease",
              boxSizing: "border-box",
            }}
            placeholder="Search subject or description..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = theme.primary;
              e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = theme.border;
              e.currentTarget.style.boxShadow = "none";
            }}
          />
          {search && (
            <button
              onClick={() => { setSearch(""); setCurrentPage(1); }}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: theme.textMuted,
                padding: "4px",
                display: "flex",
                alignItems: "center",
                fontSize: "14px",
              }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Status Filters */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "12px", fontWeight: 600, color: theme.textMuted }}>Status:</span>
          <FilterButton label="All" isActive={statusFilter === "All"} onClick={() => { setStatusFilter("All"); setCurrentPage(1); }} theme={theme} />
          <FilterButton label="Open" isActive={statusFilter === "open"} onClick={() => { setStatusFilter("open"); setCurrentPage(1); }} theme={theme} />
          <FilterButton label="In Progress" isActive={statusFilter === "in_progress"} onClick={() => { setStatusFilter("in_progress"); setCurrentPage(1); }} theme={theme} />
          <FilterButton label="Closed" isActive={statusFilter === "closed"} onClick={() => { setStatusFilter("closed"); setCurrentPage(1); }} theme={theme} />
        </div>
      </div>

      {/* Source Filters */}
      <div style={{
        display: "flex",
        gap: "6px",
        flexWrap: "wrap",
        marginBottom: "20px",
        padding: "6px",
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: "12px",
        width: "fit-content",
      }}>
        {["All", "user", "resturant", "rider"].map((source) => (
          <button
            key={source}
            onClick={() => { setSourceFilter(source); setCurrentPage(1); }}
            style={{
              padding: "7px 18px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: 500,
              background: sourceFilter === source ? theme.primary : "transparent",
              color: sourceFilter === source ? "#fff" : theme.textSecondary,
              border: "none",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (sourceFilter !== source) {
                e.currentTarget.style.background = theme.surfaceLighter;
                e.currentTarget.style.color = theme.textPrimary;
              }
            }}
            onMouseLeave={(e) => {
              if (sourceFilter !== source) {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = theme.textSecondary;
              }
            }}
          >
            {source === "All" ? "📊 All" : source === "user" ? "👤 Users" : source === "resturant" ? "🍽️ Restaurants" : "🚴 Riders"}
          </button>
        ))}
      </div>

      {/* Tickets Table */}
      <div style={{
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: `0 4px 12px ${theme.primary}08`,
      }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
            <thead>
              <tr style={{ background: theme.surfaceLighter }}>
                <th style={{ padding: "14px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}`, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.5px" }}>ID</th>
                <th style={{ padding: "14px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}`, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.5px" }}>Source</th>
                <th style={{ padding: "14px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}`, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.5px" }}>Subject</th>
                <th style={{ padding: "14px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}`, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.5px" }}>Status</th>
                <th style={{ padding: "14px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}`, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.5px" }}>Date</th>
                <th style={{ padding: "14px 16px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}`, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.5px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTickets.map((ticket) => (
                <tr
                  key={ticket.id}
                  style={{
                    borderBottom: `1px solid ${theme.border}`,
                    transition: "background 0.15s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = theme.surfaceHover || theme.surfaceLighter)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td style={{ padding: "14px 16px", fontSize: "13px", borderBottom: `1px solid ${theme.border}` }}>
                    <span style={{ fontWeight: 700, color: theme.primary, fontFamily: "monospace" }}>#{ticket.id}</span>
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: "13px", borderBottom: `1px solid ${theme.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span style={{ color: theme.textPrimary, fontWeight: 500 }}>{getUserDisplayName(ticket)}</span>
                      <SourceBadge userTable={ticket.user_table} theme={theme} />
                    </div>
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: "13px", color: theme.textPrimary, borderBottom: `1px solid ${theme.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span>{ticket.subject}</span>
                      {ticket.screenshot && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: theme.warning, fontSize: "11px", fontWeight: 500, background: `${theme.warning}1a`, padding: "2px 8px", borderRadius: "10px" }}>
                          <AttachmentIcon /> Attachment
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "14px 16px", borderBottom: `1px solid ${theme.border}` }}>
                    <StatusBadge status={ticket.ticketstatus} theme={theme} />
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: "12px", color: theme.textMuted, borderBottom: `1px solid ${theme.border}` }}>
                    {new Date(ticket.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td style={{ padding: "14px 16px", borderBottom: `1px solid ${theme.border}` }}>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button
                        style={{
                          background: `${theme.primary}15`,
                          border: `1px solid ${theme.primary}30`,
                          cursor: "pointer",
                          padding: "6px 8px",
                          color: theme.primary,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "6px",
                          transition: "all 0.2s ease",
                        }}
                        onClick={() => handleViewTicket(ticket)}
                        title="View Ticket"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `${theme.primary}25`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = `${theme.primary}15`;
                        }}
                      >
                        <EyeIcon />
                      </button>
                      <button
                        style={{
                          background: `${theme.error || theme.danger}15`,
                          border: `1px solid ${theme.error || theme.danger}30`,
                          cursor: "pointer",
                          padding: "6px 8px",
                          color: theme.error || theme.danger,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "6px",
                          transition: "all 0.2s ease",
                        }}
                        title="Delete"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `${theme.error || theme.danger}25`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = `${theme.error || theme.danger}15`;
                        }}
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {paginatedTickets.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div style={{ padding: "60px", textAlign: "center" }}>
                      <div style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        background: `${theme.primary}1a`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 16px",
                        color: theme.primary,
                      }}>
                        <TicketIcon />
                      </div>
                      <h3 style={{ color: theme.textPrimary, marginBottom: "8px", fontSize: "16px", fontWeight: 600 }}>
                        No Tickets Found
                      </h3>
                      <p style={{ color: theme.textMuted, fontSize: "13px", margin: 0 }}>
                        {search || statusFilter !== "All" || sourceFilter !== "All"
                          ? "Try adjusting your filters"
                          : "No support tickets available"}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredTickets.length > itemsPerPage && (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 20px",
            borderTop: `1px solid ${theme.border}`,
            background: theme.surfaceLighter,
            flexWrap: "wrap",
            gap: "12px",
          }}>
            <span style={{ fontSize: "12px", color: theme.textSecondary }}>
              Showing <strong style={{ color: theme.textPrimary }}>{Math.min((currentPage - 1) * itemsPerPage + 1, filteredTickets.length)}</strong> to <strong style={{ color: theme.textPrimary }}>{Math.min(currentPage * itemsPerPage, filteredTickets.length)}</strong> of <strong style={{ color: theme.textPrimary }}>{filteredTickets.length}</strong> tickets
            </span>
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "6px",
                  border: `1px solid ${theme.border}`,
                  background: theme.surface,
                  color: currentPage === 1 ? theme.textMuted : theme.textPrimary,
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: currentPage === 1 ? 0.5 : 1,
                  transition: "all 0.2s ease",
                }}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return pageNum <= totalPages && (
                  <button
                    key={pageNum}
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "6px",
                      border: currentPage === pageNum ? `1px solid ${theme.primary}` : `1px solid ${theme.border}`,
                      background: currentPage === pageNum ? theme.primary : theme.surface,
                      color: currentPage === pageNum ? "#fff" : theme.textSecondary,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: currentPage === pageNum ? 700 : 500,
                      fontSize: "12px",
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "6px",
                  border: `1px solid ${theme.border}`,
                  background: theme.surface,
                  color: currentPage === totalPages ? theme.textMuted : theme.textPrimary,
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: currentPage === totalPages ? 0.5 : 1,
                  transition: "all 0.2s ease",
                }}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Ticket Dialog */}
      {isViewDialogOpen && selectedTicket && (
        <ViewTicketDialog
          onClose={() => {
            setIsViewDialogOpen(false);
            setSelectedTicket(null);
            fetchTickets();
          }}
          ticket={selectedTicket}
          theme={theme}
        />
      )}
    </div>
  );
};

export default TicketsTable;